/**
 * VoiceTabsStrip — vibe-designer 1.163.0 voice tabs (WS-A Φ3).
 *
 * Mirrors `ChatPanelPi.tsx:1505-1554` tab-strip pattern for the voice panel.
 * Sits ABOVE the VoicePanel header per worldview D-A4.
 *
 * Reads voiceTabsStore (tabs + activeTabId + actions); the per-tab voice state
 * (status/transcript/history/settings) lives in voiceStore's byTabId map, gated
 * by activeTabId here.
 *
 * Returns null when there are zero tabs OR when tenantId is null (pre-hydrate),
 * mirroring ChatPanelPi's empty-strip behavior.
 *
 * Touch targets honor [@media(pointer:coarse)] min-w/h 44px per Phase 2b
 * tap-target discipline (project-wide 2026-05-13 ship).
 */
import { useVoiceTabsStore, VOICE_TABS_MAX } from '@/stores/voiceTabsStore';
import { useVoiceStore } from '@/stores/voiceStore';

interface VoiceTabsStripProps {
  tenantId: string | null;
}

export function VoiceTabsStrip({ tenantId }: VoiceTabsStripProps) {
  const tabs = useVoiceTabsStore((s) => s.tabs);
  const activeTabId = useVoiceTabsStore((s) => s.activeTabId);
  const newTab = useVoiceTabsStore((s) => s.newTab);
  const closeTab = useVoiceTabsStore((s) => s.closeTab);
  const setActiveTab = useVoiceTabsStore((s) => s.setActiveTab);
  // 1.164.2 — subscribe to voiceStore.byTabId so the strip re-renders when
  // any tab's voiceAgentName changes. Display format: `${label} - ${voiceAgentName}`
  // (e.g., "Voice tab 1 - Bridge"). Pre-hydrate fallback: byTabId entry missing
  // → suffix is empty → strip shows just the label (graceful degradation).
  const byTabId = useVoiceStore((s) => s.byTabId);

  if (!tenantId) return null;
  if (tabs.length === 0) return null;

  const atCap = tabs.length >= VOICE_TABS_MAX;

  return (
    <div
      data-testid="voice-tabs-strip"
      className="flex items-center gap-1 px-2 py-1 border-b border-vibe-border bg-vibe-bg-secondary text-xs shrink-0 overflow-x-auto"
    >
      {tabs.map((tab) => {
        const active = tab.id === activeTabId;
        // 1.164.2 — voiceAgentName suffix per user UX directive.
        // aria-label on close button stays anchored to bare label (not displayLabel)
        // so screen readers say "Close Voice tab 1" not "Close Voice tab 1 - Bridge".
        const voiceAgentName = byTabId[tab.id]?.settings.voiceAgentName?.trim() || '';
        const displayLabel = voiceAgentName
          ? `${tab.label} - ${voiceAgentName}`
          : tab.label;
        return (
          <div
            key={tab.id}
            data-testid={`voice-tab-${tab.id}`}
            data-active={active ? 'true' : 'false'}
            className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer ${
              active
                ? 'bg-vibe-accent/20 border border-vibe-accent/40 text-vibe-text'
                : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary'
            }`}
            onClick={() => setActiveTab(tenantId, tab.id)}
          >
            <span className="truncate max-w-[200px]">{displayLabel}</span>
            <span
              role="button"
              aria-label={`Close ${tab.label}`}
              data-testid={`voice-tab-close-${tab.id}`}
              className="opacity-50 hover:opacity-100 px-1 [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:flex [@media(pointer:coarse)]:items-center [@media(pointer:coarse)]:justify-center"
              onClick={(e) => {
                e.stopPropagation();
                void closeTab(tenantId, tab.id);
              }}
            >
              ×
            </span>
          </div>
        );
      })}
      <button
        data-testid="voice-new-tab"
        aria-label="New voice tab"
        disabled={atCap}
        onClick={() => { newTab(tenantId); }}
        className="px-2 py-1 text-vibe-text-secondary hover:bg-vibe-bg-tertiary rounded disabled:opacity-50 disabled:cursor-not-allowed [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
        title={
          atCap
            ? `Tab cap reached (${VOICE_TABS_MAX}). Close a tab to add another.`
            : 'New voice tab'
        }
      >
        +
      </button>
    </div>
  );
}
